import { useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Banner } from '../../components/Banner';
import { Input } from '../../components/Input';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { useCashCandidates, type CashCandidate } from '../../api/admin';
import {
  CASH_METHOD_LABEL,
  recordCashPayment,
  type CashMethod,
  type RecordCashResult,
} from '../../api/billing';
import { formatDate } from '../../lib/format';

const STATUS_TONE: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  active: 'success',
  trial: 'success',
  past_due: 'warning',
  suspended: 'error',
};

/** Manual cash payment — search a business, see what it owes, record the payment. */
export function CashEntry() {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<CashCandidate | null>(null);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<CashMethod>('manual_cash');
  const [receiptRef, setReceiptRef] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<RecordCashResult | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  const { data: candidates = [], isFetching } = useCashCandidates(debounced);

  function pick(c: CashCandidate) {
    setSelected(c);
    setAmount(c.owedMinor > 0 ? String(c.owedMinor / 100) : '');
    setError(null);
    setDone(null);
  }

  function reset() {
    setSelected(null);
    setQuery('');
    setDebounced('');
    setAmount('');
    setReceiptRef('');
    setNote('');
    setError(null);
    setDone(null);
  }

  async function onRecord() {
    if (!selected) return;
    const amountMinor = parseCashAmountMinor(amount);
    if (amountMinor === null) {
      setError('Enter a valid amount in BDT (a positive number).');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await recordCashPayment({
        businessId: selected.id,
        amountMinor,
        method,
        receiptRef: receiptRef.trim() || undefined,
        note: note.trim() || undefined,
      });
      setDone(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the payment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <PageHeader
        eyebrow="Platform"
        title="Record cash payment"
        subtitle="Mark a subscription paid when the customer paid by cash off-platform."
      />

      <div className="space-y-5">
        <Card className="p-5 sm:p-6">
          <CardHeader title="Find the business" />

          {!selected ? (
            <>
              <Input
                label="Business"
                required
                placeholder="Search by business name or slug (min 2 characters)…"
                prefix={<Search className="w-4 h-4" />}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />
              {debounced.trim().length >= 2 && (
                <div className="mt-3 rounded-lg border border-zinc-200 dark:border-zinc-700 divide-y divide-zinc-200 dark:divide-zinc-700 overflow-hidden">
                  {isFetching ? (
                    <div className="p-3 space-y-2">
                      <Skeleton className="h-10 rounded" />
                      <Skeleton className="h-10 rounded" />
                    </div>
                  ) : candidates.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-zinc-500 dark:text-zinc-400">
                      No business matches “{debounced.trim()}”.
                    </div>
                  ) : (
                    candidates.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => pick(c)}
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition"
                      >
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-zinc-950 dark:text-zinc-50 truncate">{c.name}</span>
                          <span className="block text-xs text-zinc-500 dark:text-zinc-400 truncate">
                            {c.plan ?? 'No plan'}
                          </span>
                        </span>
                        <span className="flex items-center gap-3 flex-shrink-0">
                          <Badge tone={STATUS_TONE[c.status] ?? 'neutral'}>{c.status.replace(/_/g, ' ')}</Badge>
                          {c.owedMinor > 0 ? (
                            <span className="text-sm">
                              <span className="text-zinc-500 dark:text-zinc-400 mr-1">Owes</span>
                              <Money minor={c.owedMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50 font-medium" />
                            </span>
                          ) : (
                            <span className="text-xs text-zinc-500 dark:text-zinc-400">Nothing owed</span>
                          )}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div>
                  <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50">{selected.name}</div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                    {selected.plan ? `Plan: ${selected.plan}` : 'No plan'}
                    {selected.pastDueSince ? ` · Past due since ${formatDate(selected.pastDueSince)}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge tone={STATUS_TONE[selected.status] ?? 'neutral'}>{selected.status.replace(/_/g, ' ')}</Badge>
                  <span className="text-sm">
                    <span className="text-zinc-500 dark:text-zinc-400 mr-1">Owed:</span>
                    <Money minor={selected.owedMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50 font-medium" />
                  </span>
                  <button
                    type="button"
                    onClick={reset}
                    aria-label="Choose a different business"
                    className="w-7 h-7 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>

        {selected && (
          <Card className="p-5 sm:p-6">
            <CardHeader title="Payment details" />
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  label="Amount (BDT)"
                  required
                  prefix={<span className="text-sm">৳</span>}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  hint={selected.owedMinor > 0 ? 'Prefilled with the open balance — adjust if the customer paid a different amount.' : undefined}
                />
                <div>
                  <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Method</label>
                  <select
                    value={method}
                    onChange={(e) => setMethod(e.target.value as CashMethod)}
                    className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand"
                  >
                    {(Object.keys(CASH_METHOD_LABEL) as CashMethod[]).map((m) => (
                      <option key={m} value={m}>{CASH_METHOD_LABEL[m]}</option>
                    ))}
                  </select>
                </div>
              </div>
              <Input
                label="Payment reference"
                value={receiptRef}
                onChange={(e) => setReceiptRef(e.target.value)}
                hint="Optional — office receipt #, bKash TrxID, bank slip, etc."
              />
              <div>
                <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Note</label>
                <textarea
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional note stored with the payment"
                  className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 placeholder-zinc-400 dark:placeholder-zinc-500 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand resize-none"
                />
              </div>
            </div>
          </Card>
        )}

        {error && <Banner tone="error" title="Couldn’t record the payment">{error}</Banner>}
        {done ? (
          <Banner tone="success" title="Payment recorded">
            Invoice <span className="mono text-xs">{done.invoiceNo}</span> created — the business is now{' '}
            <strong>{done.status}</strong>
            {done.currentPeriodEnd ? <> (paid through {formatDate(done.currentPeriodEnd)})</> : null}.
          </Banner>
        ) : selected ? (
          <Banner tone="info">
            Records the payment, creates a <strong>paid</strong> invoice, extends the subscription one billing period
            (from the later of today / the current period end), reactivates the business if it was suspended, and
            notifies the POS.
          </Banner>
        ) : null}

        {selected && (
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="ghost" onClick={reset} disabled={submitting}>
              {done ? 'Record another' : 'Cancel'}
            </Button>
            <Button onClick={onRecord} loading={submitting} disabled={submitting || done !== null}>
              {done ? 'Recorded' : 'Record payment'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Parse the BDT amount field to integer minor units (poisha); null if not a positive number. */
export function parseCashAmountMinor(amountBdt: string): number | null {
  const n = Number(amountBdt.trim());
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
}
